import { Routes } from '@angular/router';

import { OauthCallbackComponent } from '@mark/common-lib/components';

import { LangRouteString, RouteString } from './core/enums/route-string';
import { orderAuthGuard } from './core/guards/order-auth.guard';
import { orderStatusGuard } from './core/guards/order-status.guard';
import { pendingChangesGuard } from './core/guards/pending-changes.guard';
import { questionnaireGuard } from './core/guards/questionnaire.guard';
import { langResolver } from './core/resolvers/lang.resolver';
import { orderResolver } from './core/resolvers/order.resolver';
import { planResolver } from './core/resolvers/plan.resolver';
import { questionnaireResolver } from './core/resolvers/questionnaire.resolver';
import { aliPagesRoutes } from './pages/ali-pages/ali-pages.routes';
import { IMainPageConfig } from './pages/main.config';
import { provideQuestionnaireStore } from './pages/questionnaire/core/store';
import { IQuestionnaireConfig } from './pages/questionnaire/questionnaire.config';

const childrenRoutes: Routes = [
  {
    path: ``,
    redirectTo: `${RouteString.MAIN}`,
    pathMatch: 'full',
  },
  {
    path: `${RouteString.MAIN}`,
    data: {
      config: { assessmentPath: RouteString.QUESTIONNAIRE, homePath: RouteString.MAIN } satisfies IMainPageConfig,
    },
    loadComponent: () => import('./pages/main.component').then((m) => m.MainPageComponent),
  },
  {
    path: `${RouteString.VMAIN}`,
    loadChildren: () => import('./pages/val-quiz/val-quiz.routes').then((m) => m.valQuizRoutes),
  },
  // Ali landing variants (`ali-a` / `ali-b`) — both roots + their children live in ali-pages.routes.ts.
  ...aliPagesRoutes,
  {
    path: `${RouteString.PLAN}`,
    resolve: {
      item: planResolver,
    },
    loadComponent: () => import('./pages/plan/plan.component').then((m) => m.PlanPageComponent),
  },
  {
    path: `${RouteString.PAYMENT_SUCCESSFUL}`,
    loadComponent: () =>
      import('./pages/payment-successful/payment-successful.component').then((m) => m.PaymentSuccessfulComponent),
  },
  {
    path: `${RouteString.FINAL}`,
    loadComponent: () => import('./pages/final/final.component').then((m) => m.FinalComponent),
  },
  {
    path: `${RouteString.ANALYZING}`,
    loadComponent: () =>
      import('./pages/loading-progress/loading-progress.component').then((m) => m.LoadingProgressComponent),
  },
  // {
  //   path: `${RouteString.LOG_IN}`,
  //   loadComponent: () => import('./pages/sign-in/sign-in.component').then((m) => m.SignInComponent),
  // },
  {
    path: `${RouteString.ORDER}`,
    canActivate: [orderStatusGuard, orderAuthGuard],
    data: {
      config: { homePath: RouteString.MAIN } as IMainPageConfig,
    },
    resolve: {
      item: orderResolver,
    },
    loadComponent: () => import('./pages/order/order.component').then((m) => m.OrderComponent),
  },
  {
    path: `${RouteString.QUESTIONNAIRE}`,
    data: {
      config: { homePath: RouteString.MAIN, assessmentPath: RouteString.QUESTIONNAIRE } satisfies IQuestionnaireConfig,
    },
    loadComponent: () => import('./pages/questionnaire/questionnaire.component').then((m) => m.QuestionnaireComponent),
    canActivate: [questionnaireGuard],
    canDeactivate: [pendingChangesGuard],
    resolve: {
      item: questionnaireResolver,
    },
    providers: [provideQuestionnaireStore()],
  },
  {
    path: `${RouteString.NOT_FOUND}`,
    loadComponent: () => import('./pages/not-found/not-found.component').then((m) => m.NotFoundComponent),
  },
  {
    path: `${RouteString.ERROR}`,
    loadComponent: () => import('./pages/error/error.component').then((m) => m.ErrorComponent),
  },
  { path: '**', redirectTo: `${RouteString.NOT_FOUND}` },
];

export const routes: Routes = [
  {
    path: 'login/oauth/callback',
    component: OauthCallbackComponent,
  },
  {
    path: ``,
    redirectTo: `${LangRouteString.EN}`,
    pathMatch: 'full',
  },
  {
    path: `${RouteString.NOT_FOUND}`,
    redirectTo: `${LangRouteString.EN}/${RouteString.NOT_FOUND}`,
    pathMatch: 'full',
  },
  {
    path: `${RouteString.ERROR}`,
    redirectTo: `${LangRouteString.EN}/${RouteString.ERROR}`,
    pathMatch: 'full',
  },
  {
    path: `:lang`,
    resolve: {
      item: langResolver,
    },
    children: childrenRoutes,
  },
  { path: '**', redirectTo: `${LangRouteString.EN}/${RouteString.NOT_FOUND}` },
];
